import axios, { AxiosRequestConfig } from 'axios';
import { message } from 'antd';
import { API_PREFIX } from '../utils/constants';
import type {
  ContractActionRequest, ContractApiResponse, ContractDelayRequest,
  ContractOperationResult, ContractOrder, ContractOrderDetail,
} from '../types';

export interface StoreRequestConfig extends AxiosRequestConfig {
  silent?: boolean;
  silentError?: boolean;
  silentPermission?: boolean;
}

const isPermissionDenied = (data: any) => {
  return data?.code === 403 || data?.permissionDenied === true;
};

const shouldShowError = (config?: AxiosRequestConfig) => {
  const requestConfig = (config || {}) as StoreRequestConfig;
  return !requestConfig.silent && !requestConfig.silentError;
};

const shouldShowPermission = (config?: AxiosRequestConfig) => {
  const requestConfig = (config || {}) as StoreRequestConfig;
  return !requestConfig.silent && !requestConfig.silentPermission;
};

const buildPermissionResponse = (data: any) => ({
  ...(data || {}),
  code: 403,
  data: data?.data || null,
  msg: data?.msg || '权限不足，无法执行该操作',
  permissionDenied: true,
});

const buildApiError = (data: any) => {
  const error = new Error(data?.msg || '请求失败') as Error & { responseData?: any };
  error.responseData = data;
  return error;
};

const buildUploadConfig = (config?: StoreRequestConfig): StoreRequestConfig => ({
  ...config,
  headers: {
    ...((config?.headers as Record<string, unknown>) || {}),
    'Content-Type': undefined,
    'content-type': undefined,
  },
});

const request = axios.create({
  baseURL: API_PREFIX,
  timeout: 30000,
  headers: { 'Content-Type': 'application/json' },
});

// 请求拦截器：自动携带 token
request.interceptors.request.use((config) => {
  const token = localStorage.getItem('store_token');
  if (token) {
    config.headers['token'] = token;
  }
  return config;
});

// 响应拦截器：统一错误处理
request.interceptors.response.use(
  (response) => {
    // Blob 响应（文件下载）直接返回 data
    if (response.config.responseType === 'blob') {
      return response.data;
    }
    const res = response.data;
    if (res.code === 200) {
      return res;
    }
    // Token 过期
    if (res.code === 401) {
      localStorage.removeItem('store_token');
      localStorage.removeItem('store_user');
      window.location.href = '/login';
      return Promise.reject(buildApiError(res));
    }
    if (isPermissionDenied(res)) {
      if (shouldShowPermission(response.config)) {
        message.warning(res.msg || '权限不足，无法执行该操作');
      }
      return buildPermissionResponse(res);
    }
    if (shouldShowError(response.config)) {
      message.error(res.msg || '请求失败');
    }
    return Promise.reject(buildApiError(res));
  },
  (error) => {
    if (error?.response?.status === 403 || isPermissionDenied(error?.response?.data)) {
      const data = buildPermissionResponse(error?.response?.data);
      if (shouldShowPermission(error?.config || error?.response?.config)) {
        message.warning(data.msg);
      }
      return data;
    }
    if (shouldShowError(error?.config)) {
      message.error('网络异常，请稍后重试');
    }
    return Promise.reject(error);
  }
);

export default request;

// API 接口封装
export const authApi = {
  login: (data: { mobile: string; password: string }) =>
    request.post('/auth/login', data),
  selectStore: (data: { store_selection_token: string; store_id: string }) =>
    request.post('/auth/select-store', data),
  availableStores: (data: { keyword: string; page: number; pagesize: number }) =>
    request.post('/auth/available-stores', data),
  switchStore: (data: { target_store_id: string; expected_store_id: string }) =>
    request.post('/auth/switch-store', data),
  getUserInfo: (config?: StoreRequestConfig) =>
    request.post('/auth/user-info', {}, config),
  changePassword: (data: { old_password: string; new_password: string }) =>
    request.post('/auth/change-password', data),
};

export const dashboardApi = {
  getData: (config?: StoreRequestConfig) => request.post('/dashboard', {}, config),
};

// ==================== 商品管理 ====================
export const productApi = {
  create: (data: Record<string, unknown>) =>
    request.post('/product/create', data),
  list: (data: Record<string, unknown>) =>
    request.post('/product/list', data),
  detail: (product_code: string) =>
    request.post('/product/detail', { product_code }),
  update: (data: Record<string, unknown>) =>
    request.post('/product/update', data),
  delete: (product_code: string) =>
    request.post('/product/delete', { product_code }),
  batchDelete: (product_codes: string[]) =>
    request.post('/product/batch-delete', { product_codes }),
  updateStatus: (product_code: string, status: number) =>
    request.post('/product/update-status', { product_code, status }),
  batchUpdateStatus: (product_codes: string[], status: number) =>
    request.post('/product/batch-update-status', { product_codes, status }),
  updateField: (product_code: string, field: string, value: unknown) =>
    request.post('/product/update-field', { product_code, field, value }),
  copy: (product_code: string) =>
    request.post('/product/copy', { product_code }),
  updateStock: (sku_code: string, stock: number) =>
    request.post('/product/update-stock', { sku_code, stock }),
  batchUpdateStock: (items: { sku_code: string; stock: number }[]) =>
    request.post('/product/batch-update-stock', { items }),
  batchUpdateMemberPrice: (data: { product_code: string; member_price: number; sku_codes?: string[] }) =>
    request.post('/product/batch-update-member-price', data),
  skuList: (product_code: string) =>
    request.post('/product/sku-list', { product_code }),
  barcodeLookup: (barcode: string) =>
    request.post('/product/barcode-lookup', { barcode }),
};

// ==================== 分类管理 ====================
export const categoryApi = {
  create: (data: { category_name: string; sort?: number; category_image?: string }) =>
    request.post('/category/create', data),
  list: () =>
    request.post('/category/list'),
  update: (data: { category_code: string; category_name: string; sort?: number; category_image?: string }) =>
    request.post('/category/update', data),
  delete: (category_code: string) =>
    request.post('/category/delete', { category_code }),
};

// ==================== 品牌管理 ====================
export const brandApi = {
  create: (data: Record<string, unknown>) =>
    request.post('/brand/create', data),
  list: (data: Record<string, unknown>) =>
    request.post('/brand/list', data),
  detail: (brand_id: number) =>
    request.post('/brand/detail', { brand_id }),
  all: () =>
    request.post('/brand/all'),
  update: (data: Record<string, unknown>) =>
    request.post('/brand/update', data),
  delete: (brand_id: number) =>
    request.post('/brand/delete', { brand_id }),
};

// ==================== 供应商管理 ====================
export const supplierApi = {
  create: (data: Record<string, unknown>) =>
    request.post('/supplier/create', data),
  list: (data: Record<string, unknown>) =>
    request.post('/supplier/list', data),
  detail: (supplier_id: number) =>
    request.post('/supplier/detail', { supplier_id }),
  all: () =>
    request.post('/supplier/all'),
  update: (data: Record<string, unknown>) =>
    request.post('/supplier/update', data),
  delete: (supplier_id: number) =>
    request.post('/supplier/delete', { supplier_id }),
};

// ==================== 批量导入 ====================
const unwrapImportData = (response: any) => response?.data || response;

const normalizeImportValidatePayload = (response: any) => {
  const data = unwrapImportData(response);
  if (data?.error === true && typeof data.batch_id !== 'string') {
    const messageText = data.msg || data.message || '文件校验未通过';
    return {
      ...data,
      batch_id: '',
      total_rows: Number(data.total_rows || 0),
      success_count: Number(data.success_count || 0),
      error_count: Number(data.error_count || 1),
      errors: Array.isArray(data.errors) && data.errors.length > 0
        ? data.errors
        : [{ row: 0, field: '文件', value: '', message: messageText }],
      preview: [],
      error: true,
    };
  }
  if (!data || typeof data.batch_id !== 'string') {
    throw new Error('导入校验返回数据异常，请重试');
  }
  return {
    ...data,
    total_rows: Number(data.total_rows || 0),
    success_count: Number(data.success_count || 0),
    error_count: Number(data.error_count || 0),
    errors: Array.isArray(data.errors) ? data.errors : [],
    preview: Array.isArray(data.preview) ? data.preview : [],
  };
};

const normalizeImportConfirmPayload = (response: any) => {
  const data = unwrapImportData(response);
  if (!data || typeof data.imported_count !== 'number' || !Array.isArray(data.product_codes)) {
    throw new Error('导入结果返回数据异常，请刷新导入记录确认结果');
  }
  return data;
};

export const importApi = {
  downloadTemplate: (template_type: string) =>
    request.post('/import/template', { template_type }, { responseType: 'blob' }),
  validate: async (formData: FormData) => {
    const response = await request.post('/import/validate', formData, buildUploadConfig({
      timeout: 120000,
    }));
    return normalizeImportValidatePayload(response);
  },
  confirm: async (batch_id: string) => {
    const response = await request.post('/import/confirm', { batch_id }, { timeout: 120000 });
    return normalizeImportConfirmPayload(response);
  },
  errorFile: (batch_id: string) =>
    request.post('/import/error-file', { batch_id }, { responseType: 'blob' }),
  records: (data: {
    page?: number; page_size?: number;
    status?: number; template_type?: string;
    keyword?: string; start_date?: string; end_date?: string;
  }) =>
    request.post('/import/records', data).then(unwrapImportData),
  detail: (batch_id: string) =>
    request.post('/import/detail', { batch_id }).then(unwrapImportData),
  cancel: (batch_id: string) =>
    request.post('/import/cancel', { batch_id }),
};

// ==================== 文件上传 ====================
export interface BatchImageUploadItem {
  index: number;
  file_name: string;
  status: 'success' | 'error';
  url?: string;
  key?: string;
  material_code?: string;
  asset_code?: string;
  thumb_url?: string;
  card_url?: string;
  detail_url?: string;
  web_thumb_url?: string;
  variant_status?: string;
  error?: string;
}

export interface BatchImageUploadResult {
  total: number;
  success_count: number;
  error_count: number;
  items: BatchImageUploadItem[];
  errors: BatchImageUploadItem[];
}

export interface BatchImageUploadOptions {
  concurrency?: number;
  onItemStart?: (params: { index: number; file: File }) => void;
  onItemSuccess?: (item: BatchImageUploadItem) => void;
  onItemError?: (item: BatchImageUploadItem) => void;
}

const IMAGE_BATCH_MAX_COUNT = 20;
const MB = 1024 * 1024;
const IMAGE_BATCH_MAX_FILE_SIZE = 50 * MB;
const IMAGE_BATCH_MAX_TOTAL_SIZE = 300 * MB;
const IMAGE_LARGE_FILE_CONCURRENCY_THRESHOLD = 10 * MB;
const IMAGE_CLIENT_COMPRESS_MAX_PIXELS = 16_000_000;
const IMAGE_CLIENT_COMPRESS_TYPES = ['image/jpeg', 'image/webp'];
const IMAGE_BATCH_ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp', 'gif'];
const IMAGE_BATCH_ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

const getFileExtension = (fileName: string): string => {
  const segments = fileName.split('.');
  return segments.length > 1 ? segments[segments.length - 1].toLowerCase() : '';
};

const validateImageBatchFiles = (files: File[]) => {
  if (!files.length) {
    throw new Error('请选择要上传的图片');
  }
  if (files.length > IMAGE_BATCH_MAX_COUNT) {
    throw new Error('一次最多上传20张图片');
  }

  const totalSize = files.reduce((sum, file) => sum + file.size, 0);
  if (totalSize > IMAGE_BATCH_MAX_TOTAL_SIZE) {
    throw new Error('单批总大小不能超过300MB');
  }

  const invalidTypeFile = files.find((file) => {
    const extension = getFileExtension(file.name);
    return !IMAGE_BATCH_ALLOWED_EXTENSIONS.includes(extension) || (
      Boolean(file.type) && !IMAGE_BATCH_ALLOWED_MIME_TYPES.includes(file.type)
    );
  });
  if (invalidTypeFile) {
    throw new Error('仅支持 jpg/jpeg/png/webp/gif 格式图片');
  }

  const oversizedFile = files.find((file) => file.size > IMAGE_BATCH_MAX_FILE_SIZE);
  if (oversizedFile) {
    throw new Error(`单张图片不能超过50MB：${oversizedFile.name}`);
  }
};

const canvasToBlob = (canvas: HTMLCanvasElement, type: string, quality: number) =>
  new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, type, quality));

const compressImageFile = async (file: File): Promise<File> => {
  if (!IMAGE_CLIENT_COMPRESS_TYPES.includes(file.type)) {
    return file;
  }
  if (typeof document === 'undefined' || typeof createImageBitmap !== 'function') {
    return file;
  }

  try {
    const bitmap = await createImageBitmap(file);
    if (bitmap.width * bitmap.height > IMAGE_CLIENT_COMPRESS_MAX_PIXELS) {
      bitmap.close?.();
      return file;
    }
    const width = Math.max(1, bitmap.width);
    const height = Math.max(1, bitmap.height);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close?.();
      return file;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close?.();

    const outputType = file.type === 'image/webp' ? 'image/webp' : 'image/jpeg';
    for (const quality of [0.9, 0.85, 0.8]) {
      const blob = await canvasToBlob(canvas, outputType, quality);
      if (blob && blob.size > 0 && blob.size < file.size) {
        return new File([blob], file.name, {
          type: outputType,
          lastModified: file.lastModified,
        });
      }
    }
    return file;
  } catch (error) {
    return file;
  }
};

const buildFileUploadFormData = async (file: File, prefix?: string, group_code?: string) => {
  const uploadFile = await compressImageFile(file);
  const formData = new FormData();
  formData.append('file', uploadFile);
  if (prefix) formData.append('prefix', prefix);
  if (group_code) formData.append('group_code', group_code);
  return formData;
};

const buildRawFileUploadFormData = (file: File, prefix?: string, group_code?: string) => {
  const formData = new FormData();
  formData.append('file', file);
  if (prefix) formData.append('prefix', prefix);
  if (group_code) formData.append('group_code', group_code);
  return formData;
};

const compressImageFormData = async (formData: FormData): Promise<FormData> => {
  const file = formData.get('file');
  if (!(file instanceof File)) {
    return formData;
  }
  const uploadFile = await compressImageFile(file);
  if (uploadFile === file) {
    return formData;
  }

  const nextFormData = new FormData();
  formData.forEach((value, key) => {
    nextFormData.append(key, key === 'file' ? uploadFile : value);
  });
  return nextFormData;
};

export interface UploadImagePayload {
  url: string;
  key: string;
  material_code?: string;
  asset_code?: string;
  width?: number;
  height?: number;
  source_url?: string;
  thumb_url?: string;
  card_url?: string;
  detail_url?: string;
  banner_url?: string;
  web_thumb_url?: string;
  variants?: Record<string, any>;
  variant_status?: string;
}

const unwrapUploadImagePayload = (response: unknown): UploadImagePayload => {
  const payload = response as any;
  if (payload?.data?.url && payload?.data?.key) {
    return payload.data;
  }
  if (payload?.url && payload?.key) {
    return payload;
  }
  throw new Error(payload?.msg || '上传返回数据异常');
};

export const uploadApi = {
  image: async (formData: FormData) =>
    request.post('/upload/image', await compressImageFormData(formData), buildUploadConfig({
      timeout: 60000,
    })),
  video: (formData: FormData) =>
    request.post('/upload/video', formData, buildUploadConfig({
      timeout: 120000,
    })),
  uploadImage: async (file: File, prefix?: string, group_code?: string) =>
    request.post('/upload/image', await buildFileUploadFormData(file, prefix, group_code), buildUploadConfig({
      timeout: 60000,
    })),
  uploadVideo: async (file: File, prefix?: string, group_code?: string) =>
    request.post('/upload/video', buildRawFileUploadFormData(file, prefix, group_code), buildUploadConfig({
      timeout: 120000,
    })),
  uploadImages: async (
    files: File[],
    prefix?: string,
    group_code?: string,
    options: BatchImageUploadOptions = {},
  ): Promise<BatchImageUploadResult> => {
    const fileList = Array.from(files);
    validateImageBatchFiles(fileList);

    const hasLargeImage = fileList.some((file) => file.size > IMAGE_LARGE_FILE_CONCURRENCY_THRESHOLD);
    const maxConcurrency = hasLargeImage ? 2 : 3;
    const concurrency = Math.min(Math.max(options.concurrency || maxConcurrency, 1), maxConcurrency);
    const items: BatchImageUploadItem[] = new Array(fileList.length);
    let nextIndex = 0;

    const uploadOne = async (index: number) => {
      const file = fileList[index];
      options.onItemStart?.({ index, file });
      try {
        const response = await uploadApi.uploadImage(file, prefix, group_code);
        const payload = unwrapUploadImagePayload(response);
        const item: BatchImageUploadItem = {
          index,
          file_name: file.name,
          status: 'success',
          url: payload.url,
          key: payload.key,
          material_code: payload.material_code,
          asset_code: payload.asset_code,
          thumb_url: payload.thumb_url,
          card_url: payload.card_url,
          detail_url: payload.detail_url,
          web_thumb_url: payload.web_thumb_url,
          variant_status: payload.variant_status,
        };
        items[index] = item;
        options.onItemSuccess?.(item);
      } catch (error: any) {
        const item: BatchImageUploadItem = {
          index,
          file_name: file.name,
          status: 'error',
          error: error?.message || '上传失败',
        };
        items[index] = item;
        options.onItemError?.(item);
      }
    };

    const workers = Array.from({ length: Math.min(concurrency, fileList.length) }, async () => {
      while (nextIndex < fileList.length) {
        const index = nextIndex;
        nextIndex += 1;
        await uploadOne(index);
      }
    });

    await Promise.all(workers);
    const normalizedItems = items.filter(Boolean);
    const errors = normalizedItems.filter((item) => item.status === 'error');

    return {
      total: fileList.length,
      success_count: normalizedItems.length - errors.length,
      error_count: errors.length,
      items: normalizedItems,
      errors,
    };
  },
};

// ==================== 服务项目管理 ====================
const templateRequest = async (action: string, data: Record<string, unknown>) => {
  const result: any = await request.post(`/service/information-template/${action}`, data);
  // The shared interceptor resolves 403 responses; a write must not report those as success.
  if (result.code !== 200) throw buildApiError(result);
  return result;
};

export const informationTemplateApi = {
  list: (data: Record<string, unknown> = {}) => templateRequest('list', data),
  detail: (id: number) => templateRequest('detail', { id }),
  save: (data: Record<string, unknown>) => templateRequest('save', data),
  copy: (id: number) => templateRequest('copy', { id }),
  status: (id: number, version: number, status: number) => templateRequest('status', { id, version, status }),
  delete: (id: number, version: number) => templateRequest('delete', { id, version }),
};

export const serviceApi = {
  getList: (params: { page?: number; pagesize?: number; service_type?: number; status?: number; keyword?: string }) =>
    request.post('/service/list', params),
  getDetail: (service_id: string) =>
    request.post('/service/detail', { service_id }),
  create: (data: Record<string, unknown>) =>
    request.post('/service/create', data),
  update: (data: Record<string, unknown>) =>
    request.post('/service/update', data),
  updateStatus: (service_id: string, status: number) =>
    request.post('/service/update-status', { service_id, status }),
  batchUpdateMemberPrice: (data: { service_id: string; member_price: number; sku_ids?: string[] }) =>
    request.post('/service/batch-update-member-price', data),
  delete: (service_id: string) =>
    request.post('/service/delete', { service_id }),
  // 时段配置
  getSlots: () =>
    request.post('/service/timeslot/list'),
  batchSetSlots: (slots: Record<string, unknown>[]) =>
    request.post('/service/timeslot/save', { slots }),
  deleteSlot: (slot_id: string) =>
    request.post('/service/timeslot/delete', { slot_id }),
  // 预约配置
  getBookingConfig: () =>
    request.post('/service/booking-config/get'),
  saveBookingConfig: (configs: Record<string, string>) =>
    request.post('/service/booking-config/save', { configs }),
  resetBookingConfig: () =>
    request.post('/service/booking-config/reset'),
};

// ==================== 寄养配置 ====================
export const boardingConfigApi = {
  getConfig: () =>
    request.post('/service/boarding-config/get'),
  saveConfig: (capacity: number) =>
    request.post('/service/boarding-config/save', { capacity }),
  getHolidayList: (year?: string) =>
    request.post('/service/holiday/list', { year }),
  addHoliday: (holiday_date: string, holiday_name: string) =>
    request.post('/service/holiday/add', { holiday_date, holiday_name }),
  batchAddHolidays: (holidays: { holiday_date: string; holiday_name: string }[]) =>
    request.post('/service/holiday/batch-add', { holidays }),
  deleteHoliday: (id: number) =>
    request.post('/service/holiday/delete', { id }),
};

// ==================== 品种管理 ====================
export const breedApi = {
  list: (params?: { category_id?: number; keyword?: string }) =>
    request.post('/breed/list', params || {}),
  create: (data: { category_id: number; breed_name: string }) =>
    request.post('/breed/create', data),
  update: (data: { id: number; breed_name: string; category_id?: number }) =>
    request.post('/breed/update', data),
  delete: (id: number) =>
    request.post('/breed/delete', { id }),
};

// ==================== 员工管理 ====================
export const employeeApi = {
  list: (params?: { keyword?: string; role_code?: string }) =>
    request.post('/employee/list', params || {}),
  create: (data: { emp_name: string; mobile: string; role_code: string }) =>
    request.post('/employee/create', data),
  update: (data: { emp_id: string; emp_name?: string; mobile?: string; status?: number; role_code?: string }) =>
    request.post('/employee/update', data),
  delete: (emp_id: string) =>
    request.post('/employee/delete', { emp_id }),
  resetPassword: (emp_id: string) =>
    request.post('/employee/reset-password', { emp_id }),
};

// ==================== 权限配置 ====================
export const storePermissionApi = {
  menuTree: () =>
    request.post('/permission/menu-tree'),
  list: () =>
    request.post('/permission/list'),
};

export const storeRoleApi = {
  list: () =>
    request.post('/role/list'),
  detail: (role_code: string) =>
    request.post('/role/detail', { role_code }),
  updatePermissions: (data: { role_code: string; pages: Array<{ menu_id: number; button_scope: 'all' | 'custom'; button_codes: string[] }> }) =>
    request.post('/role/update-permissions', data),
};

// ==================== 门店信息 ====================
export const storeInfoApi = {
  getInfo: () => request.post('/store/info'),
  update: (data: Record<string, unknown>) => request.post('/store/update', data),
};

// ==================== 会员管理 ====================
export const memberApi = {
  getList: (params: Record<string, unknown>) =>
    request.post('/member/list', params),
  getDetail: (member_id: string) =>
    request.post('/member/detail', { member_id }),
  update: (params: { member_id: string; nickname?: string; gender?: number; birthday?: string | null }) =>
    request.post('/member/update', params),
  create: (params: Record<string, unknown>) =>
    request.post('/member/create', params),
  getBalanceOverview: () =>
    request.post('/member/balance/overview'),
  getBalanceLogs: (params: Record<string, unknown>) =>
    request.post('/member/balance/logs', params),
  getMemberBalanceLogs: (params: { member_id: string; change_type?: number; time_start?: string; time_end?: string; page?: number; pagesize?: number }) =>
    request.post('/member/balance-logs', params),
  getMemberOrders: (params: { member_id: string; page?: number; pagesize?: number }) =>
    request.post('/member/orders', params),
  getMemberServiceOrders: (params: { member_id: string; page?: number; pagesize?: number }) =>
    request.post('/member/service-orders', params),
  getAddresses: (member_id: string) =>
    request.post('/member/addresses', { member_id }),
  getBrowseHistory: (params: { member_id: string; page?: number; pagesize?: number }) =>
    request.post('/member/browse-history', params),
};

// ==================== 宠物管理 ====================
export const petApi = {
  getCategoryList: () =>
    request.post('/pet/category-list'),
  getBreedList: (params?: { category_id?: number }) =>
    request.post('/breed/list', params || {}),
  getMemberPetBreedList: (params?: { category_id?: number }) =>
    request.post('/pet/breed-list', params || {}),
  getMemberPets: (member_id: string) =>
    request.post('/member/pets', { member_id }),
  getLiveCandidateList: (params: { page?: number; page_size?: number; keyword?: string }) =>
    request.post('/pet/live-candidate/list', params),
  getLiveCandidateDetail: (live_pet_id: number) =>
    request.post('/pet/live-candidate/detail', { live_pet_id }),
  createFromLive: (params: {
    member_id: string;
    live_pet_id: number;
    pet_nickname: string;
    weight_kg: number;
    is_sterilized: number;
  }) => request.post('/pet/create-from-live', params),
  create: (params: Record<string, unknown>) =>
    request.post('/pet/create', params),
  update: (params: Record<string, unknown>) =>
    request.post('/pet/update', params),
  delete: (pet_id: number) =>
    request.post('/pet/delete', { pet_id }),
  getHealthRecords: (pet_id: number, record_type: number) =>
    request.post('/pet/health/list', { pet_id, record_type }),
  saveHealthRecord: (params: Record<string, unknown>) =>
    request.post('/pet/health/save', params),
  deleteHealthRecord: (record_id: number) =>
    request.post('/pet/health/delete', { record_id }),
};

// ==================== 字典 ====================
export const dictApi = {
  getByType: (dict_type: string) =>
    request.post('/dict/list', { dict_type }),
};

// ==================== 活体管理 ====================
export const livePetApi = {
  list: (params: { page?: number; page_size?: number; keyword?: string; status?: number; category_id?: number; breed_id?: number; gender?: number }) =>
    request.post('/live-pet/list', params),
  detail: (id: number) =>
    request.post('/live-pet/detail', { id }),
  create: (data: Record<string, unknown>) =>
    request.post('/live-pet/create', data),
  update: (data: Record<string, unknown>) =>
    request.post('/live-pet/update', data),
  delete: (id: number) =>
    request.post('/live-pet/delete', { id }),
  vaccineList: (pet_id: number) =>
    request.post('/live-pet/vaccine/list', { pet_id }),
  vaccineCreate: (data: { pet_id: number; vaccine_date: string; vaccine_brand: string; vaccine_detail?: string }) =>
    request.post('/live-pet/vaccine/create', data),
  vaccineDelete: (id: number) =>
    request.post('/live-pet/vaccine/delete', { id }),
  dewormingList: (pet_id: number) =>
    request.post('/live-pet/deworming/list', { pet_id }),
  dewormingCreate: (data: { pet_id: number; deworming_date: string; deworming_type: number; deworming_brand: string }) =>
    request.post('/live-pet/deworming/create', data),
  dewormingDelete: (id: number) =>
    request.post('/live-pet/deworming/delete', { id }),
};

export const livePetTransferApi = {
  targetPreview: (target_store_id: string) =>
    request.post('/live-pet/transfer/target-preview', { target_store_id }),
  create: (data: { pet_id: number; target_store_id: string; remark?: string; request_id: string }) =>
    request.post('/live-pet/transfer/create', data),
  incomingList: (data: { page?: number; page_size?: number; status?: string; keyword?: string }) =>
    request.post('/live-pet/transfer/incoming/list', data),
  historyList: (data: { page?: number; page_size?: number; direction?: string; status?: string; keyword?: string; start_date?: string; end_date?: string }) =>
    request.post('/live-pet/transfer/history/list', data),
  detail: (transfer_no: string) =>
    request.post('/live-pet/transfer/detail', { transfer_no }),
  accept: (data: { transfer_no: string; request_id: string }) =>
    request.post('/live-pet/transfer/accept', data),
  reject: (data: { transfer_no: string; reason: string; request_id: string }) =>
    request.post('/live-pet/transfer/reject', data),
  cancel: (data: { transfer_no: string; reason?: string; request_id: string }) =>
    request.post('/live-pet/transfer/cancel', data),
};

// ==================== 收银台 ====================
export const cashierApi = {
  search: (keyword: string) =>
    request.post('/cashier/search', { keyword }),
  productSkus: (product_code: string) =>
    request.post('/cashier/product-skus', { product_code }),
  serviceSkus: (service_id: string) =>
    request.post('/cashier/service-skus', { service_id }),
  serviceOptions: (service_id: string) =>
    request.post('/cashier/service-options', { service_id }),
  serviceQuote: (data: { service_id: string; sku_id?: string; addon_ids?: string[]; use_member_price?: boolean; member_id?: string }) =>
    request.post('/cashier/service-quote', data),
  productList: (params: { page?: number; page_size?: number; keyword?: string; category_code?: string }) =>
    request.post('/cashier/product-list', params),
  serviceList: (params: { page?: number; page_size?: number; keyword?: string; service_type?: number }) =>
    request.post('/cashier/service-list', params),
  catalog: (params: { mode?: 'all' | 'product' | 'service'; page?: number; page_size?: number; keyword?: string; category_code?: string; service_type?: number }) =>
    request.post('/cashier/catalog', params),
  bootstrap: (params: { mode?: 'all' | 'product' | 'service'; page?: number; page_size?: number; keyword?: string; category_code?: string; service_type?: number; pending_limit?: number }) =>
    request.post('/cashier/bootstrap', params),
  memberSearch: (keyword: string) =>
    request.post('/cashier/member-search', { keyword }),
  checkout: (data: Record<string, unknown>, options?: { silentError?: boolean }) =>
    request.post('/cashier/checkout', data, options?.silentError ? ({ silentError: true } as any) : undefined),
  pendingCreate: (data: Record<string, unknown>) =>
    request.post('/cashier/pending/create', data),
  pendingList: (params?: { limit?: number }) =>
    request.post('/cashier/pending/list', params || {}),
  pendingRestore: (id: number) =>
    request.post('/cashier/pending/restore', { id }),
  pendingDelete: (id: number) =>
    request.post('/cashier/pending/delete', { id }),
  todayOrders: () =>
    request.post('/cashier/today-orders'),
  priceLog: (data: Record<string, unknown>) =>
    request.post('/cashier/price-log', data),
  orderList: (params: Record<string, unknown>) =>
    request.post('/cashier/order-list', params),
  orderDetail: (order_code: string) =>
    request.post('/cashier/order-detail', { order_code }),
};

export const mallOrderApi = {
  list: (params: Record<string, unknown>) =>
    request.post('/mall-order/list', params),
  detail: (order_code: string) =>
    request.post('/mall-order/detail', { order_code }),
  adjustAmount: (data: { order_code: string; new_amount: number; adjust_reason: string }) =>
    request.post('/mall-order/adjust-amount', data),
  pickup: (data: { order_code: string; pickup_code: string }) =>
    request.post('/mall-order/pickup', data),
  void: (data: { order_code: string; reason: string }) =>
    request.post('/mall-order/void', data),
};

// 洗护订单
export const washOrderApi = {
  list: (params: Record<string, unknown>) =>
    request.post('/wash-order/list', params),
  detail: (order_code: string) =>
    request.post('/wash-order/detail', { order_code }),
  accept: (order_code: string) =>
    request.post('/wash-order/accept', { order_code }),
  checkin: (order_code: string) =>
    request.post('/wash-order/checkin', { order_code }),
  verify: (data: { order_code: string; verify_code: string }) =>
    request.post('/wash-order/verify', data),
  adjustAmount: (data: { order_code: string; new_amount: number; adjust_reason: string }) =>
    request.post('/wash-order/adjust-amount', data),
  cancel: (data: { order_code: string; reason: string }) =>
    request.post('/wash-order/cancel', data),
};

// 寄养订单
export const boardingOrderApi = {
  list: (params: Record<string, unknown>) =>
    request.post('/boarding-order/list', params),
  detail: (order_code: string) =>
    request.post('/boarding-order/detail', { order_code }),
  accept: (order_code: string) =>
    request.post('/boarding-order/accept', { order_code }),
  checkIn: (data: { order_code: string }) =>
    request.post('/boarding-order/check-in', data),
  verify: (data: { order_code: string; verify_code: string }) =>
    request.post('/boarding-order/verify', data),
  checkOut: (order_code: string) =>
    request.post('/boarding-order/check-out', { order_code }),
  updateDates: (data: { order_code: string; check_in_date: string; check_out_date: string; update_reason: string }) =>
    request.post('/boarding-order/update-dates', data),
  adjustAmount: (data: { order_code: string; new_amount: number; adjust_reason: string }) =>
    request.post('/boarding-order/adjust-amount', data),
  cancel: (data: { order_code: string; reason: string }) =>
    request.post('/boarding-order/cancel', data),
  logs: (order_code: string) =>
    request.post('/boarding-order/logs', { order_code }),
};

// 医疗订单（门店端只读）与用券配置
export const medicalApi = {
  getServiceOrders: (params: Record<string, unknown>) =>
    request.post('/medical/service-order/list', params),
  getServiceOrderDetail: (order_code: string) =>
    request.post('/medical/service-order/detail', { order_code }),
  getMedicalConfig: () =>
    request.post('/medical/config/get'),
  saveMedicalConfig: (data: { configs: Array<{ payment_type: string; coupon_enabled: boolean }> }) =>
    request.post('/medical/config/save', data),
  getMedicalOrders: (params: { page?: number; pagesize?: number; business_status?: number; payment_type?: string; keyword?: string }) =>
    request.post('/medical/order/list', params),
  getMedicalOrderDetail: (order_code: string) =>
    request.post('/medical/order/detail', { order_code }),
};

// 网页消息通知
export const notificationApi = {
  poll: (include_list = false) =>
    request.post('/notification/poll', { include_list }, {
      silent: true,
      silentError: true,
      silentPermission: true,
    } as StoreRequestConfig),
  list: (params: Record<string, unknown>) =>
    request.post('/notification/list', params),
  streamUrl: () => {
    const token = localStorage.getItem('store_token') || '';
    return `${API_PREFIX}/notification/stream?token=${encodeURIComponent(token)}`;
  },
};

// ==================== 签约订单 ====================
export const contractApi = {
  list: (params: Record<string, unknown>) =>
    request.post<unknown, ContractApiResponse<{ list: ContractOrder[]; total: number }>>('/contract/list', params),
  detail: (card_id: string) =>
    request.post<unknown, ContractApiResponse<ContractOrderDetail>>('/contract/detail', { card_id }),
  terminate: (data: ContractActionRequest) =>
    request.post<unknown, ContractApiResponse<ContractOperationResult>>('/contract/terminate', data, { silent: true } as StoreRequestConfig),
  delay: (data: ContractDelayRequest) =>
    request.post<unknown, ContractApiResponse<ContractOperationResult>>('/contract/delay', data, { silent: true } as StoreRequestConfig),
};

// ==================== 结算管理 ====================
export const settlementApi = {
  overview: () =>
    request.post('/settlement/overview', {}),
  list: (params: Record<string, unknown>) =>
    request.post('/settlement/list', params),
  detail: (settlement_id: number) =>
    request.post('/settlement/detail', { settlement_id }),
};

// ==================== 工资管理（首期仅登记） ====================
export const payrollApi = {
  payeeList: (params: Record<string, unknown>) =>
    request.post('/payroll/payee/list', params),
  savePayee: (data: Record<string, unknown>) =>
    request.post('/payroll/payee/save', data),
  removePayee: (emp_id: string) =>
    request.post('/payroll/payee/remove', { emp_id }),
  batchList: (params: Record<string, unknown>) =>
    request.post('/payroll/batch/list', params),
  batchDetail: (batch_id: number) =>
    request.post('/payroll/batch/detail', { batch_id }),
  saveBatch: (data: Record<string, unknown>) =>
    request.post('/payroll/batch/save', data),
};

// ==================== 门店码管理 ====================
export const storeCodeApi = {
  getInfo: () =>
    request.post('/store-code/info'),
  getStoreQrcode: () =>
    request.post('/store-code/store-qrcode'),
  getEmpQrcode: (emp_id: string) =>
    request.post('/store-code/emp-qrcode', { emp_id }),
  getEmpList: () =>
    request.post('/store-code/emp-list'),
  deleteStoreQrcode: () =>
    request.post('/store-code/delete-store-qrcode'),
  deleteEmpQrcode: (emp_id: string) =>
    request.post('/store-code/delete-emp-qrcode', { emp_id }),
};

// ==================== 素材管理 ====================
export const materialApi = {
  getGroupList: (material_type: string) =>
    request.post('/material/group/list', { material_type }),
  getPublicGroupList: (material_type: string) =>
    request.post('/material/public-group/list', { material_type }),
  createGroup: (data: { group_name: string; material_type: string; sort?: number }) =>
    request.post('/material/group/create', data),
  updateGroup: (data: { group_code: string; group_name?: string; sort?: number }) =>
    request.post('/material/group/update', data),
  deleteGroup: (group_code: string) =>
    request.post('/material/group/delete', { group_code }),
  getMaterialList: (data: { group_code: string; page?: number; pagesize?: number }) =>
    request.post('/material/list', data),
  getPublicMaterialList: (data: { group_code: string; page?: number; pagesize?: number }) =>
    request.post('/material/public/list', data),
  createMaterial: (data: { group_code: string; material_url: string; material_type?: string; material_name?: string; thumbnail_url?: string; file_size?: number }) =>
    request.post('/material/create', data),
  deleteMaterial: (material_code: string) =>
    request.post('/material/delete', { material_code }),
  batchDeleteMaterials: (material_codes: string[]) =>
    request.post('/material/batch-delete', { material_codes }),
  moveMaterials: (material_codes: string[], target_group_code: string) =>
    request.post('/material/move', { material_codes, target_group_code }),
};

// ==================== 推广关系 ====================
export const promotionApi = {
  commissionList: (params: Record<string, unknown>) =>
    request.post('/promotion/commission/list', params),
  commissionDetail: (commission_no: string) =>
    request.post('/promotion/commission/detail', { commission_no }),
  relationList: (params: Record<string, unknown>) =>
    request.post('/promotion/relation/list', params),
  attemptList: (params: Record<string, unknown>) =>
    request.post('/promotion/attempt/list', params),
  consumptionList: (params: { relation_id: number; page: number; pagesize: number }) =>
    request.post('/promotion/consumption/list', params),
};
